import { DateTime } from 'luxon'
import { MouseEvent, ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { PrivateCompanyIndicator } from '@components/PrivateCompanyIndicator'
import Button from '@components/formControls/Button'
import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import { ProfileAlert } from '@modules/alerts/types'
import { getProfileIsUpdated } from '@modules/alerts/utils'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import ContactsHistoryPopover from '@modules/contacts/components/ContactsHistoryPopover'
import ContactsOverviewCard from '@modules/contacts/components/ContactsOverviewCard'
import { CompanyContactHistoryCounts, Meeting, MeetingStatus, UPCOMING_MEETINGS } from '@modules/contacts/types'
import MarkAsViewedButton from '@modules/savedSearch/components/MarkAsViewedButton'
import { AnyProfile, AnyResultProfile, Delegate, HistoryProfileEvent, ProfileType } from '@modules/search/types'
import ShortlistProfileButton from '@modules/shortlist/components/ShortlistProfileButton'
import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { createEmptyArray, truncateWithDots } from '@shared/utils'

import FieldSectionRow, { FieldSectionProps } from './FieldSectionRow'
import {
  MainTextWrapper,
  NewItemButtonWrapper,
  ProfileCardContentContainer,
  ProfileCardFooterContainer,
  ProfileCardHeaderContainer,
  ProfileDate,
  ProfileHeaderTitleContainer,
  ResultCardItemInner,
  ResultCardProfileTitle,
  SecondaryTextWrapper,
  StyledProfileLogo,
  ThirdTextWrapper,
  UpdatedLabel,
} from './ResultProfileList.styles'

/** temp thing, sanitizing will be done on BE */
const sanitizeString = (str: string) =>
  str
    ?.replaceAll(/(<([^>]+)>)/gi, '')
    .replaceAll(/&amp;/g, '&')
    .replaceAll(/&nbsp;/g, ' ')

export interface ResultProfileItemProps {
  resultProfile: AnyResultProfile
  fields: {
    field: string
    label: string
    format?: (value: string | string[]) => string
    render?: (profile: AnyProfile, events?: HistoryProfileEvent[]) => ReactNode | undefined
  }[]
  renderTitle: (profile: AnyProfile) => string
  fieldsGrid: { lg: number[]; md?: number[] }
  onContactClick?: (e: Nullable<MouseEvent<HTMLElement>>) => void
  onReadMoreClick?: () => void
  companyContactCounts?: Nullable<CompanyContactHistoryCounts>
  recentMeeting?: Nullable<Meeting>
  isContactButtonEnabled?: boolean
  hasContacted?: boolean
  isViewed?: boolean
  isNew?: boolean
  savedSearchId?: number
  profileAlert?: ProfileAlert
}

const ResultProfileItem = (props: ResultProfileItemProps) => {
  const {
    resultProfile,
    companyContactCounts,
    fields,
    renderTitle,
    fieldsGrid,
    onContactClick,
    onReadMoreClick,
    recentMeeting,
    isContactButtonEnabled,
    hasContacted,
    isViewed: rawIsViewed,
    isNew,
    savedSearchId,
    profileAlert,
  } = props
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)

  const theme = useTheme()
  const isMdScreen = useMediaQuery(theme.breakpoints.down(1250))

  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()
  const { latest: profile, events, latestCompany, hasPublicProfile } = resultProfile
  const profileType = resultProfile['@type']

  const rawIsUpdated = useMemo(
    () =>
      getProfileIsUpdated({
        profile,
        profileAlert,
        type: profileType,
        assetLatestCompany: latestCompany,
        events,
      }),
    [profileType, profileAlert, profile, latestCompany],
  )

  const isUpdated = hasAccessToAlerts && rawIsUpdated

  const [messageStreamAnchorRef, setMessageStreamAnchorRef] = useState<Nullable<HTMLElement>>(null)
  const isViewed = rawIsViewed ?? true
  const preparedFields = useMemo(
    () =>
      fields
        ?.map(fieldOptions => {
          const { format, label, render } = fieldOptions

          if (render) {
            return {
              value: render(profile, events),
              label,
            }
          }

          const rawValue = profile[fieldOptions.field as keyof AnyProfile] as string
          const formatted = format ? format(rawValue) : rawValue
          const sanitized = sanitizeString(formatted)

          return {
            value: sanitized && truncateWithDots(sanitized, 400),
            label,
          }
        })
        .filter(i => i.value),
    [fields, profile, events],
  )

  const upcomingMeeting = UPCOMING_MEETINGS.includes(recentMeeting?.status as MeetingStatus) ? recentMeeting : null

  const columns = useMemo(() => {
    const gridData = isMdScreen && fieldsGrid.md ? fieldsGrid.md : fieldsGrid.lg
    const columnsArr = createEmptyArray(gridData.length)

    const { result } = columnsArr.reduce<{ result: FieldSectionProps['fields'][]; indexed: number }>(
      acc => {
        const rows = createEmptyArray(gridData[acc.result.length])
          .map((_, rIdx) => preparedFields[acc.indexed + rIdx])
          .filter(Boolean)

        return {
          result: [...acc.result, rows] as FieldSectionProps['fields'][],
          indexed: acc.indexed + rows.length,
        }
      },
      { result: [], indexed: 0 },
    )

    if ((companyContactCounts || upcomingMeeting) && result.length >= 2) {
      const lastColumnFields = result[result.length - 1]
      const prevColumnFields = result[result.length - 2]

      if (lastColumnFields && prevColumnFields) {
        result[result.length - 2] = [...prevColumnFields, ...lastColumnFields]
        result.pop()
      }
    }

    return result
  }, [preparedFields, isMdScreen, fieldsGrid, companyContactCounts, upcomingMeeting])

  const handleDetailsClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(e => {
    setMessageStreamAnchorRef(e.target as HTMLElement)
  }, [])

  const companyId = profileType === ProfileType.company ? profile.id : (profile as Delegate).companyId
  const showNewInSavedSearchButton = !isViewed && savedSearchId
  return (
    <ResultCardItemInner $isPrimary={!isViewed || isUpdated || isNew} data-testid="search-result-item-inner">
      {!showNewInSavedSearchButton && isUpdated && <UpdatedLabel>{t(translationKeys.UPDATED)}</UpdatedLabel>}
      {!showNewInSavedSearchButton && !isUpdated && isNew && <UpdatedLabel>{t(translationKeys.NEW)}</UpdatedLabel>}

      <ProfileCardHeaderContainer>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ProfileHeaderTitleContainer>
            <StyledProfileLogo profileData={profile} profileType={profileType} width={43} showAltText={false} circleView />

            <ResultCardProfileTitle variant="h2" onClick={onReadMoreClick}>
              {renderTitle(profile)}
            </ResultCardProfileTitle>
            {!!profile && profile.isPrivate && <PrivateCompanyIndicator />}
            <ProfileDate variant="body1">
              {DateTime.fromISO(profile.updatedAt || profile.createdAt).toFormat('MMM dd, yyyy')}
            </ProfileDate>
          </ProfileHeaderTitleContainer>

          <Box display="flex" ml="10px">
            {hasAccessToAlerts && (
              <ProfileAlertButton profile={resultProfile} savedProfileAlert={profileAlert} isPrivate={!hasPublicProfile} />
            )}
            <ShortlistProfileButton profile={profile} events={resultProfile.events} />
          </Box>
        </Box>
      </ProfileCardHeaderContainer>

      <ProfileCardContentContainer>
        <Grid container columns={{ md: 12, lg: 12, xs: 12 }} wrap="nowrap">
          <Grid item md={7} lg={5}>
            <FieldSectionRow wrapper={MainTextWrapper} fields={columns[0]} />
          </Grid>
          <Grid item md={5} lg={4}>
            <FieldSectionRow wrapper={SecondaryTextWrapper} fields={columns[1]} />
          </Grid>
          <Grid item md={0} lg={3}>
            {companyContactCounts || upcomingMeeting ? (
              <ContactsOverviewCard
                companyId={companyId}
                counts={companyContactCounts}
                meeting={upcomingMeeting}
                onDetailsClick={handleDetailsClick}
                isHighlighted={hasAccessToAlerts && !!profileAlert?.hasNewActivity}
              />
            ) : (
              <FieldSectionRow wrapper={ThirdTextWrapper} fields={columns[2]} />
            )}
          </Grid>
        </Grid>
      </ProfileCardContentContainer>

      <ProfileCardFooterContainer>
        <Button variant="outlined" onClick={onReadMoreClick} data-testid="view_full_profile_btn">
          {t(translationKeys.SEARCH_RESULTS_ITEM_READ_MORE_BTN)}
        </Button>
        {isContactButtonEnabled && hasAccessToChat && hasPublicProfile && !isEcommerce && (
          <ContactedBubble hasContacted={hasContacted}>
            <Button variant="contained" onClick={onContactClick} data-testid="contact_profile_btn">
              {t(translationKeys.CONTACT_BUTTON)}
            </Button>
          </ContactedBubble>
        )}
      </ProfileCardFooterContainer>

      {recentMeeting && messageStreamAnchorRef && (
        <ContactsHistoryPopover
          companyId={companyId}
          recentMeeting={recentMeeting}
          anchor={messageStreamAnchorRef}
          setAnchor={setMessageStreamAnchorRef}
        />
      )}
      {showNewInSavedSearchButton && (
        <NewItemButtonWrapper>
          <MarkAsViewedButton savedSearchId={savedSearchId} profileId={profile.id} />
        </NewItemButtonWrapper>
      )}
    </ResultCardItemInner>
  )
}

export default memo(ResultProfileItem)
